import { useEffect, useState, type FormEvent } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { loadDatasetFreshness } from '../../services/freshnessService'
import { formatUpdatedAt } from '../../utils/format'
import { freshnessStatusLabel } from '../../utils/freshness'
import { useLocale, type MessageKey } from '../../i18n/locale'

const homeNavigation: Array<{ key: string; label: MessageKey; href: string }> = [
  { key: 'today', label: 'nav.today', href: '/#today' },
  { key: 'changing', label: 'nav.changing', href: '/#changing' },
  { key: 'watch', label: 'nav.watch', href: '/#watch' },
  { key: 'finance', label: 'nav.finance', href: '/finance#results' },
  { key: 'technology', label: 'nav.technology', href: '/technology#results' },
  { key: 'world', label: 'nav.world', href: '/world#results' },
  { key: 'sports', label: 'nav.sports', href: '/sports#results' },
  { key: 'entertainment', label: 'nav.entertainment', href: '/entertainment#results' },
  { key: 'crypto', label: 'nav.crypto', href: '/crypto#results' },
]

function isHomeNavigationActive(key: string, pathname: string, search: string, hash: string) {
  const params = new URLSearchParams(search)
  const category = params.get('category')

  if (['finance', 'technology', 'world', 'sports', 'entertainment', 'crypto'].includes(key)) {
    return pathname === `/${key}` || (pathname === '/' && category === key)
  }

  if (pathname !== '/') return false
  if (category) return false
  return key === (hash.replace('#', '') || 'today')
}
export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, setLocale, t } = useLocale()
  const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(location.search).get('q') ?? '')
  const [freshness, setFreshness] = useState<Awaited<ReturnType<typeof loadDatasetFreshness>> | null>(null)

  useEffect(() => {
    setSearchQuery(new URLSearchParams(location.search).get('q') ?? '')
  }, [location.search])

  useEffect(() => {
    void loadDatasetFreshness().then(setFreshness).catch(() => setFreshness(null))
  }, [])

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const query = searchQuery.trim()
    navigate(query ? `/?q=${encodeURIComponent(query)}#results` : '/#today')
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="site-header-inner">
          <NavLink className="brand" to="/" aria-label={t('header.homeAria')}>
            <span className="brand-mark" aria-hidden="true">
              <span className="brand-mark-cross" />
              <span className="brand-mark-dot" />
            </span>
            <span className="brand-word">预见</span>
          </NavLink>
          <nav className="main-nav" aria-label={t('header.navAria')}>
            {homeNavigation.map((item) => {
              const active = isHomeNavigationActive(item.key, location.pathname, location.search, location.hash)
              return (
                <NavLink
                  className={active ? 'shell-nav-link shell-nav-link-active' : 'shell-nav-link'}
                  to={item.href}
                  key={item.key}
                  aria-current={active ? 'page' : undefined}
                >
                  {t(item.label)}
                </NavLink>
              )
            })}
          </nav>
          <div className="header-tools">
            <div className="header-data-status" title={t('header.dataStatusTitle')}>
              <span className="status-dot" aria-hidden="true" />
              <span>
                {freshness
                  ? t('header.dataUpdated', {
                      time: formatUpdatedAt(freshness.dataset.presentationGeneratedAt ?? freshness.dataset.eventGeneratedAt ?? freshness.dataset.catalogGeneratedAt, false, locale),
                      status: freshnessStatusLabel(freshness.current.status, locale),
                    })
                  : t('header.syncUnknown')}
              </span>
            </div>
            <form className="header-search" onSubmit={submitSearch} role="search">
              <span className="search-icon" aria-hidden="true">⌕</span>
              <input
                aria-label={t('header.searchAria')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('header.searchPlaceholder')}
                type="search"
              />
              <button className="search-submit" type="submit" aria-label={t('header.submitSearch')}>↵</button>
            </form>
            <NavLink className={({ isActive }) => isActive ? 'settings-link settings-link-active' : 'settings-link'} to="/settings">
              <span className="settings-icon" aria-hidden="true">⌘</span>
              <span>{t('nav.settings')}</span>
            </NavLink>
            <div className="language-switch" role="group" aria-label={t('header.languageAria')}>
              <button
                className={locale === 'zh-CN' ? 'language-button language-button-active' : 'language-button'}
                type="button"
                aria-pressed={locale === 'zh-CN'}
                onClick={() => setLocale('zh-CN')}
              >
                {t('header.chinese')}
              </button>
              <button
                className={locale === 'en' ? 'language-button language-button-active' : 'language-button'}
                type="button"
                aria-pressed={locale === 'en'}
                onClick={() => setLocale('en')}
              >
                {t('header.english')}
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="site-main">
        <Outlet />
      </main>
      <footer className="site-footer">
        <span><span className="footer-signal">●</span> {t('footer.board')}</span>
        <span>{t('footer.sources')}</span>
        <div className="footer-compliance" aria-label={t('footer.complianceAria')}>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">浙ICP备2026070641号</a>
          <span className="footer-compliance-separator" aria-hidden="true"> · </span>
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=33011002020477" target="_blank" rel="noopener noreferrer">浙公网安备33011002020477号</a>
        </div>
      </footer>
    </div>
  )
}
